import { copyTextToClipboard } from "./clipboard";

export const buildWhatsAppShareUrl = (
  message: string,
  phoneDigits?: string | null
): string => {
  const text = encodeURIComponent(message);
  const digits = (phoneDigits ?? "").replace(/\D/g, "");

  if (digits.length >= 10) {
    return `https://wa.me/${digits}?text=${text}`;
  }

  return `https://wa.me/?text=${text}`;
};

export const openWhatsAppShare = (
  message: string,
  phoneDigits?: string | null
): void => {
  window.open(
    buildWhatsAppShareUrl(message, phoneDigits),
    "_blank",
    "noopener,noreferrer"
  );
};

export const buildSmsShareUrl = (
  message: string,
  phoneDigits?: string | null
): string => {
  const body = encodeURIComponent(message);
  const digits = (phoneDigits ?? "").replace(/\D/g, "");

  if (digits.length >= 10) {
    return `sms:${digits}?body=${body}`;
  }

  return `sms:?body=${body}`;
};

export const openSmsShare = (
  message: string,
  phoneDigits?: string | null
): void => {
  window.location.href = buildSmsShareUrl(message, phoneDigits);
};

export const canUseNativeShare = (): boolean =>
  typeof navigator !== "undefined" && typeof navigator.share === "function";

export type NativeShareOutcome = "shared" | "aborted" | "unavailable";

export const tryNativeShare = async (payload: {
  title: string;
  text: string;
  url: string;
}): Promise<NativeShareOutcome> => {
  if (!canUseNativeShare()) {
    return "unavailable";
  }

  try {
    await navigator.share(payload);
    return "shared";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return "aborted";
    }

    return "unavailable";
  }
};

export type InviteShareChoice =
  | "copy"
  | "whatsapp"
  | "share"
  | "sms"
  | "inviteAnother"
  | "dismiss";

interface InviteSharePromptOptions {
  title: string;
  text: string;
  inviteUrl: string;
  whatsappMessage: string;
  /** Digits-only phone (with country code) to open a direct WhatsApp/SMS chat. */
  whatsappPhone?: string | null;
  labels: {
    copy: string;
    whatsapp: string;
    done: string;
    share?: string;
    sms?: string;
    inviteAnother?: string;
  };
  /** When set, confirm = invite another; other actions via custom buttons. */
  offerInviteAnother?: boolean;
}

/**
 * SweetAlert2 share dialog for membership invite links.
 * Uses dynamic import so non-invite screens do not pay the cost.
 */
export const promptInviteShare = async (
  options: InviteSharePromptOptions
): Promise<InviteShareChoice> => {
  const Swal = (await import("sweetalert2")).default;
  const inviteAnotherLabel = options.labels.inviteAnother;
  const nativeShareAvailable =
    canUseNativeShare() && Boolean(options.labels.share);
  const smsAvailable = Boolean(options.labels.sms);
  let choice: InviteShareChoice = "dismiss";

  const actionButtonsHtml = [
    nativeShareAvailable
      ? `<button type="button" class="btn btn-sm btn-primary w-100" id="adatis-invite-share">${options.labels.share}</button>`
      : "",
    `<button type="button" class="btn btn-sm btn-outline-primary w-100" id="adatis-invite-copy">${options.labels.copy}</button>`,
    `<button type="button" class="btn btn-sm btn-success w-100" id="adatis-invite-wa">${options.labels.whatsapp}</button>`,
    smsAvailable
      ? `<button type="button" class="btn btn-sm btn-outline-secondary w-100" id="adatis-invite-sms">${options.labels.sms}</button>`
      : "",
  ]
    .filter(Boolean)
    .join("");

  const result = await Swal.fire({
    title: options.title,
    html: `<p class="mb-3">${options.text}</p><div class="d-grid gap-2">${actionButtonsHtml}</div>`,
    icon: "success",
    showCancelButton: true,
    showConfirmButton: Boolean(options.offerInviteAnother && inviteAnotherLabel),
    confirmButtonText: inviteAnotherLabel,
    cancelButtonText: options.labels.done,
    confirmButtonColor: "#405189",
    cancelButtonColor: "#878a99",
    focusCancel: true,
    didOpen: () => {
      document
        .getElementById("adatis-invite-copy")
        ?.addEventListener("click", () => {
          choice = "copy";
          Swal.close();
        });

      document
        .getElementById("adatis-invite-wa")
        ?.addEventListener("click", () => {
          openWhatsAppShare(options.whatsappMessage, options.whatsappPhone);
          choice = "whatsapp";
          Swal.close();
        });

      document
        .getElementById("adatis-invite-sms")
        ?.addEventListener("click", () => {
          openSmsShare(options.whatsappMessage, options.whatsappPhone);
          choice = "sms";
          Swal.close();
        });

      document
        .getElementById("adatis-invite-share")
        ?.addEventListener("click", () => {
          void (async () => {
            const outcome = await tryNativeShare({
              title: options.title,
              text: options.whatsappMessage,
              url: options.inviteUrl,
            });

            if (outcome === "aborted") {
              return;
            }

            choice = outcome === "shared" ? "share" : "copy";
            Swal.close();
          })();
        });
    },
  });

  if (result.isConfirmed && options.offerInviteAnother) {
    return "inviteAnother";
  }

  return choice;
};

export const handleInviteShareChoice = async (
  choice: InviteShareChoice,
  inviteUrl: string,
  messages: { copySuccess: string; copyError: string }
): Promise<boolean> => {
  if (choice !== "copy") {
    return false;
  }

  const copied = await copyTextToClipboard(inviteUrl);
  return copied;
};

export { copyTextToClipboard };
